// File: src/pages/public/SkillPassportPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const IconVerified = () => (
    <span className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1 rounded-full text-[11px] font-bold ml-3 shadow-md shadow-blue-500/30 tracking-wide uppercase align-middle transform -translate-y-1">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
        </svg>
        Verified
    </span>
);

export default function SkillPassportPage() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch(`${apiBaseUrl}/api/users/public/passport/${id}`);
                if (res.ok) {
                    const result = await res.json();
                    setData(result);
                }
            } catch (err) { console.error(err); } 
            finally { setLoading(false); }
        };
        fetchData();
    }, [id, apiBaseUrl]);

    // Xử lý nút mời phỏng vấn
    const handleInvite = () => {
        if (data?.email) {
            // Mở trình soạn thảo email
            window.location.href = `mailto:${data.email}?subject=Mời phỏng vấn qua SkillMatch&body=Chào bạn, chúng tôi ấn tượng với hồ sơ SkillPassport của bạn...`;
        } else {
            alert("Ứng viên này chưa công khai Email.");
        }
    };

    if (loading) return (
        <div className="min-h-screen pt-28 flex items-center justify-center bg-[#F8FAFC]">
            <span className="material-symbols-outlined animate-spin text-3xl text-blue-600">sync</span>
        </div>
    );

    if (!data) return <div className="min-h-screen pt-28 flex items-center justify-center text-slate-500">Không tìm thấy hồ sơ.</div>;

    return (
        <div className="min-h-screen bg-[#F0F4F8] pt-24 pb-20 px-4 font-sans relative">
            
            {/* Background trang trí nhẹ */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-3xl opacity-60 translate-x-1/3 -translate-y-1/4"></div>
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-3xl opacity-60 -translate-x-1/3 translate-y-1/4"></div>
            </div>

            <div className="max-w-6xl mx-auto relative z-10">
                
                {/* 1. KHUNG COVER & AVATAR (Layout mới) */}
                <div className="bg-white rounded-t-3xl shadow-sm overflow-hidden border-b border-slate-100">
                    {/* Cover Image */}
                    <div className="h-48 bg-gradient-to-r from-blue-700 to-indigo-800 relative">
                        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '24px 24px'}}></div>
                        <div className="absolute top-6 right-6 bg-white/10 px-4 py-1.5 rounded-full text-white/90 text-xs font-bold border border-white/20 tracking-wider uppercase">
                            SkillMatch Passport
                        </div>
                    </div>
                    
                    <div className="px-8 pb-8">
                        <div className="flex flex-col md:flex-row gap-6">
                            {/* Avatar - Đặt chồng lên cover nhưng có viền trắng dày để tách biệt */}
                            <div className="-mt-16 flex-shrink-0">
                                <div className="relative">
                                    <img 
                                        src={data.profile?.avatar || "https://via.placeholder.com/150"} 
                                        alt="Avatar" 
                                        className="w-40 h-40 rounded-full border-[6px] border-white shadow-lg object-cover bg-white"
                                    />
                                    <div className="absolute bottom-3 right-3 w-5 h-5 bg-emerald-500 border-2 border-white rounded-full"></div>
                                </div>
                            </div>

                            {/* Tên và thông tin - Nằm hoàn toàn trên nền trắng */}
                            <div className="pt-4 md:pt-6 flex-1">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div>
                                        <h1 className="text-3xl font-extrabold text-slate-800 flex items-center">
                                            {data.profile?.fullName} <IconVerified />
                                        </h1>
                                        <p className="text-lg text-blue-600 font-medium mt-1">{data.profile?.headline || "Thành viên SkillMatch"}</p>
                                        
                                        <div className="flex items-center gap-6 text-sm text-slate-500 mt-3">
                                            {data.email && (
                                                <span className="flex items-center gap-1.5 hover:text-blue-600 transition-colors">
                                                    <span className="material-symbols-outlined text-[18px]">mail</span> {data.email}
                                                </span>
                                            )}
                                            {data.profile?.location && (
                                                <span className="flex items-center gap-1.5">
                                                    <span className="material-symbols-outlined text-[18px]">location_on</span> {data.profile.location}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. NỘI DUNG CHÍNH (Chia 2 cột) */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                    
                    {/* CỘT TRÁI: THÔNG TIN CHI TIẾT (Chiếm 2 phần) */}
                    <div className="lg:col-span-2 space-y-6">
                        
                        {/* Box Giới thiệu */}
                        <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <span className="w-8 h-[2px] bg-blue-600 rounded-full"></span> Giới thiệu
                            </h3>
                            <p className="text-slate-600 leading-relaxed text-[15px]">
                                {data.profile?.bio || "Chưa cập nhật giới thiệu."}
                            </p>
                        </div>

                        {/* Box Kinh nghiệm */}
                        <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                <span className="w-8 h-[2px] bg-blue-600 rounded-full"></span> Kinh nghiệm làm việc
                            </h3>
                            <div className="space-y-8">
                                {data.experience?.length > 0 ? data.experience.map((exp, i) => (
                                    <div key={i} className="flex gap-5 group">
                                        <div className="flex flex-col items-center">
                                            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center border border-blue-100 shadow-sm">
                                                <span className="material-symbols-outlined text-[20px]">business_center</span>
                                            </div>
                                            <div className="w-[2px] bg-slate-100 h-full mt-2 group-last:hidden"></div>
                                        </div>
                                        <div className="flex-1 pb-2">
                                            <h4 className="font-bold text-slate-800 text-lg">{exp.position}</h4>
                                            <div className="text-blue-600 font-medium mb-1">{exp.company}</div>
                                            <p className="text-xs text-slate-400 mb-3 bg-slate-50 inline-block px-2 py-1 rounded">
                                                {new Date(exp.startDate).getFullYear()} - {exp.current ? 'Hiện tại' : new Date(exp.endDate).getFullYear()}
                                            </p>
                                            {exp.description && <p className="text-sm text-slate-500">{exp.description}</p>}
                                        </div>
                                    </div>
                                )) : <p className="text-slate-400 italic">Chưa có thông tin.</p>}
                            </div>
                        </div>

                         {/* Box Kỹ năng */}
                         <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                <span className="w-8 h-[2px] bg-blue-600 rounded-full"></span> Kỹ năng chuyên môn
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {data.skills?.length > 0 ? data.skills.map((s, i) => (
                                    <span key={i} className="px-4 py-2 bg-slate-50 text-slate-700 hover:text-blue-700 hover:bg-blue-50 hover:border-blue-200 transition-all rounded-xl text-sm font-semibold border border-slate-200 cursor-default">
                                        {s}
                                    </span>
                                )) : <span className="text-slate-400 italic">Chưa cập nhật.</span>}
                            </div>
                        </div>
                    </div>

                    {/* CỘT PHẢI: THÀNH TÍCH (Sticky) */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24 space-y-6">
                            
                            {/* KHUNG THÀNH TÍCH MỚI */}
                            <div className="bg-white rounded-3xl shadow-lg shadow-blue-900/5 border border-white overflow-hidden">
                                <div className="bg-slate-50 p-5 border-b border-slate-100 flex justify-between items-center">
                                    <div>
                                        <h3 className="font-bold text-slate-800 text-sm uppercase">Thành tích nổi bật</h3>
                                        <p className="text-[10px] text-slate-500">Được xác thực bởi SkillMatch</p>
                                    </div>
                                    <div className="text-2xl">🏆</div>
                                </div>

                                {/* ✅ DANH SÁCH CUỘN ĐƯỢC (SCROLLABLE LIST) */}
                                <div className="p-2 space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                                    {data.topTests?.length > 0 ? data.topTests.map((test, i) => (
                                        <div key={i} className="bg-white border border-slate-100 p-3 rounded-xl hover:border-blue-200 hover:shadow-md transition-all group">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1 pr-2">
                                                    {/* Hiển thị Tên Công Ty (Title) */}
                                                    <div className="font-bold text-slate-700 text-sm line-clamp-1 group-hover:text-blue-700">
                                                        {test.title}
                                                    </div>
                                                    {/* Hiển thị Loại bài/Vị trí (SubTitle) */}
                                                    <div className="text-[10px] text-slate-400 mt-0.5">
                                                        {test.subTitle}
                                                    </div>
                                                </div>
                                                <div className="text-right bg-emerald-50 px-2 py-1 rounded-lg">
                                                    <span className="block text-lg font-black text-emerald-600 leading-none">{test.score}</span>
                                                </div>
                                            </div>
                                            {/* Ngày tháng */}
                                            <div className="text-[9px] text-slate-300 text-right">
                                                {new Date(test.submittedAt).toLocaleDateString('vi-VN')}
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="text-center py-8 text-slate-400">
                                            <p className="text-sm italic">Chưa có dữ liệu bài test.</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* CARD HÀNH ĐỘNG */}
                            <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-3xl p-6 text-white shadow-xl text-center relative overflow-hidden">
                                {/* Decor */}
                                <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>
                                
                                <h4 className="font-bold text-lg mb-2 relative z-10">Tuyển dụng ứng viên?</h4>
                                <p className="text-indigo-100 text-sm mb-5 relative z-10">Gửi email mời phỏng vấn ngay.</p>
                                
                                <button 
                                    onClick={handleInvite}
                                    className="relative z-10 w-full py-3 bg-white text-blue-700 font-bold rounded-xl shadow-lg hover:bg-blue-50 hover:scale-[1.02] active:scale-95 transition-all"
                                >
                                    Gửi lời mời
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                /* Tùy chỉnh thanh cuộn cho đẹp */
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
            `}</style>
        </div>
    );
}